import { Link } from "react-router-dom";
import ComplianceDisclosures from "@/components/ComplianceDisclosures";
import DisclaimerBand from "@/components/DisclaimerBand";
import { ArrowUpRight, ShieldCheck, Calculator, Printer, FileText, Building2, Zap } from "lucide-react";

const stats = [
  { label: "Federal + State Compliance", value: "50 STATES" },
  { label: "Tax Table Year", value: "2026" },
  { label: "Printable Check Format", value: "MICR-READY" },
];

const features = [
  { icon: Calculator, title: "Automated Tax Withholding", desc: "Federal Publication 15-T brackets, FICA, Medicare additional & per-state rates calculated per paycheck. No spreadsheets." },
  { icon: Printer, title: "Print Company Paychecks", desc: "Generate printable PDF paychecks with MICR routing lines and itemised earning statements attached. Feed pre-printed check stock or blank paper." },
  { icon: ShieldCheck, title: "Compliance Engine", desc: "SS wage base, Medicare additional threshold and per-state flat rates baked into the engine and updated annually." },
  { icon: FileText, title: "Auditable Ledger", desc: "Every run stores a frozen snapshot of employee info, gross, deductions and net. Reprint stubs any time." },
  { icon: Building2, title: "Multi-Employee Workspaces", desc: "Manage hourly + salaried employees, filing status, additional withholding and terminations in one dense table." },
  { icon: Zap, title: "One-Click Payroll Runs", desc: "Weekly, biweekly, semimonthly and monthly cycles. Preview totals, confirm, print. Under a minute per run." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-paper text-ink relative overflow-hidden">
      <div className="absolute inset-0 grid-noise pointer-events-none" />

      {/* NAV */}
      <header className="relative border-b-2 border-ink">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              data-testid="landing-logo-mark"
              src="/checkmate-logo.png"
              alt="Checkmate Payroll"
              className="h-9 w-auto"
            />
            <div className="hidden sm:block border-l-2 border-ink/20 pl-3">
              <div className="overline text-[10px] text-muted-foreground">PAYROLL · US 2026</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Federal + 51 jurisdictions</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-widest">
            <a href="#features" className="hover:underline underline-offset-4">Features</a>
            <a href="#compliance" className="hover:underline underline-offset-4">Compliance</a>
            <a href="#pricing" className="hover:underline underline-offset-4">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link data-testid="nav-login" to="/login" className="hidden sm:inline-block px-4 py-2 border-2 border-ink font-mono text-xs uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">Sign In</Link>
            <Link data-testid="nav-register" to="/register" className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2">
              Get Started <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b-2 border-ink">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline mb-6">SEC. 3401(a) · IRS PUB. 15-T · FLSA</div>
            <h1 className="font-heading font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-5">
              Enterprise Payroll.<br />
              <span className="inline-block bg-ink text-white px-3">Mathematical Precision.</span>
            </h1>
            <p className="font-mono text-base md:text-lg uppercase tracking-widest mb-6" data-testid="hero-positioning">
              Compliance-Ready Payroll Tools for US Businesses
            </p>
            <p className="max-w-2xl text-lg text-muted-foreground leading-relaxed mb-10">
              Checkmate Payroll prepares federal and state payroll and filings from the published IRS and
              state tables, and prints your company paychecks on demand.
              Built for founders, ops teams, and controllers who want the numbers to <em>reconcile to the cent</em>.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link data-testid="hero-cta-register" to="/register" className="hard-btn px-6 py-4 font-mono uppercase tracking-widest text-sm inline-flex items-center gap-3">
                Run Your First Payroll <ArrowUpRight className="w-5 h-5" />
              </Link>
              <Link data-testid="hero-cta-login" to="/login" className="px-6 py-4 border-2 border-ink bg-white font-mono uppercase tracking-widest text-sm hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-hard transition-all">
                Sign In to Console
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 relative">
            {/* Sample pay-check mock */}
            <div className="bg-white border-2 border-ink shadow-hard-lg p-6 font-mono text-xs">
              <div className="flex justify-between items-start mb-4 pb-3 border-b-2 border-ink">
                <div>
                  <div className="font-heading font-black text-sm">DEMO PAYROLL CO.</div>
                  <div className="text-[10px] text-muted-foreground">1200 Market St, San Francisco CA</div>
                </div>
                <div className="text-right">
                  <div className="overline">CHECK</div>
                  <div className="font-black text-base">00184</div>
                </div>
              </div>
              <div className="mb-3">
                <div className="overline text-[10px] mb-1">PAY TO</div>
                <div className="font-heading font-black text-base">JORDAN M. RIVERA</div>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="overline text-[10px]">NET AMOUNT</div>
                <div className="border-2 border-ink px-3 py-1 font-black">$2,148.67</div>
              </div>
              <div className="border-t-2 border-dashed border-ink pt-3 mt-4 space-y-1">
                <div className="flex justify-between"><span>Gross</span><span>$2,880.00</span></div>
                <div className="flex justify-between"><span>Federal</span><span>-$298.14</span></div>
                <div className="flex justify-between"><span>SS + Medicare</span><span>-$220.32</span></div>
                <div className="flex justify-between"><span>State (CA)</span><span>-$158.40</span></div>
                <div className="flex justify-between font-black border-t border-ink pt-1 mt-1"><span>NET</span><span>$2,148.67</span></div>
              </div>
              <div className="mt-4 text-[10px] tracking-widest">C00184C  A121000358A  9876543210C</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b-2 border-ink bg-ink text-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="md:border-r-2 md:border-white/20 last:border-r-0 md:pr-8">
              <div className="overline text-white/60 mb-3">{s.label}</div>
              <div className="font-mono font-black text-3xl">{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="border-b-2 border-ink">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20">
          <div className="mb-12">
            <div className="overline mb-3">§ FEATURES</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight max-w-2xl">Everything you need to cut a paycheck. Nothing you don't.</h2>
          </div>
          <div className="grid grid-cols-12 gap-6">
            {features.map((f, i) => (
              <div key={f.title} className={`hard-card p-8 col-span-12 md:col-span-6 lg:col-span-4 ${i === 0 ? "hard-card-shadow" : ""}`}>
                <f.icon className="w-8 h-8 mb-6" strokeWidth={2.25} />
                <div className="font-heading font-black text-xl mb-3 tracking-tight">{f.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE CALLOUT */}
      <section id="compliance" className="border-b-2 border-ink">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <div className="overline mb-3">§ COMPLIANCE</div>
            <h2 className="font-heading font-black text-4xl tracking-tight mb-3">Compliance-Ready Payroll Tools for US Businesses</h2>
            <p className="font-mono text-sm text-muted-foreground mb-6">Grounded in the published tables — not in any government affiliation.</p>
            <p className="text-muted-foreground leading-relaxed mb-6">Our withholding engine implements the 2026 IRS Publication 15-T percentage method, FICA (Social Security wage base $184,500 · Medicare 1.45% + 0.9% additional over $200k), and the published 2026 withholding tables for all 51 state jurisdictions. Checkmate prepares the documents and agency-format files; you submit them and keep the agency's confirmation.</p>
            <ul className="font-mono text-sm space-y-2">
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-ink" /> IRS Publication 15-T (2026)</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-ink" /> SSA Wage Base 2026</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-ink" /> FLSA Overtime (1.5×)</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-ink" /> State Withholding · CA / NY / TX / FL & 40+ more</li>
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="hard-card hard-card-shadow p-8">
              <div className="overline mb-4">SAMPLE · SINGLE FILER · $75,000 GROSS ANNUAL</div>
              <div className="font-mono text-sm">
                <div className="grid grid-cols-2 gap-y-3 border-b-2 border-ink pb-4">
                  <div>Gross (biweekly)</div><div className="text-right">$2,884.62</div>
                  <div>Federal Income Tax</div><div className="text-right">$298.62</div>
                  <div>Social Security 6.2%</div><div className="text-right">$178.85</div>
                  <div>Medicare 1.45%</div><div className="text-right">$41.83</div>
                  <div>California SIT 5.5%</div><div className="text-right">$158.65</div>
                </div>
                <div className="grid grid-cols-2 gap-y-3 pt-4 font-black">
                  <div>NET PAY</div><div className="text-right">$2,206.67</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING · CHECKMATE REVENUE HUB (3-tier) */}
      <section id="pricing" className="bg-ink text-white border-b-2 border-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20">
          <div className="text-center mb-14">
            <div className="overline text-warn mb-3">§ PRICING · REVENUE HUB</div>
            <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tight mb-4">Three tiers. Real ROI.</h2>
            <p className="text-white/70 max-w-2xl mx-auto">Start free with a small donation. Unlock the full compliance engine with Pro, or automate your entire back office with Pro Plus.</p>
            <p className="text-white/40 text-xs font-mono mt-4">Grandfathered pre-2026 customers keep their $1,500/mo rate forever.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Community */}
            <div className="border-2 border-white p-8 bg-ink flex flex-col" data-testid="pricing-community">
              <div className="overline text-white/60 mb-3">§ COMMUNITY</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-heading font-black text-5xl tracking-tight">$1</span>
                <span className="text-white/60 font-mono text-xs">/ mo · donation bridge</span>
              </div>
              <div className="font-mono text-[11px] text-white/50 mb-6">Solo operators &amp; non-profits</div>
              <ul className="font-mono text-sm space-y-2.5 mb-8 flex-1">
                <li className="flex items-start gap-2"><span className="text-warn">→</span> Basic time tracking</li>
                <li className="flex items-start gap-2"><span className="text-warn">→</span> Manual paychecks + PDF stubs</li>
                <li className="flex items-start gap-2"><span className="text-warn">→</span> Employee self-service portal</li>
                <li className="flex items-start gap-2"><span className="text-warn">→</span> W-2 generation</li>
                <li className="flex items-start gap-2 opacity-40"><span>×</span> Advanced tax reporting</li>
                <li className="flex items-start gap-2 opacity-40"><span>×</span> AI Office Manager</li>
              </ul>
              <Link to="/register?tier=community" data-testid="pricing-community-cta" className="block border-2 border-white bg-transparent px-6 py-3 font-mono uppercase text-xs tracking-widest text-center hover:bg-white hover:text-ink">Start with $1 →</Link>
            </div>

            {/* Pro */}
            <div className="border-2 border-warn p-8 bg-white text-ink relative flex flex-col" data-testid="pricing-pro">
              <span className="absolute -top-3 right-6 bg-warn text-ink px-3 py-1 text-[10px] uppercase tracking-widest font-black">Most Popular</span>
              <div className="overline mb-3">§ PRO</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-heading font-black text-5xl tracking-tight">$2,500</span>
                <span className="text-muted-foreground font-mono text-xs">/ month</span>
              </div>
              <div className="font-mono text-[11px] text-muted-foreground mb-6">Full compliance engine · 5-25 employee shops</div>
              <ul className="font-mono text-sm space-y-2.5 mb-8 flex-1" data-testid="pricing-pro-features">
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Everything in Community</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Federal + 50-state tax compliance</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Predictive Payroll &amp; Budget Alerts</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Quarterly Tax Summary Reports</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> HR Document E-Sign (E-SIGN Act §101)</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Mapbox Worksite Geofencing</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Twilio SMS Alert Escalation</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Stripe Connect ACH Payouts</li>
              </ul>
              <Link to="/register?tier=pro" data-testid="pricing-pro-cta" className="block border-2 border-ink bg-ink text-white px-6 py-3 font-mono uppercase text-xs tracking-widest text-center hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform">Start Pro →</Link>
            </div>

            {/* Pro Plus */}
            <div className="border-4 border-warn p-8 bg-white text-ink relative flex flex-col shadow-hard-lg" data-testid="pricing-pro-plus">
              <span className="absolute -top-3 right-6 bg-good text-white px-3 py-1 text-[10px] uppercase tracking-widest font-black">Best ROI</span>
              <div className="overline mb-3 text-warn">§ PRO PLUS</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-heading font-black text-5xl tracking-tight">$4,500</span>
                <span className="text-muted-foreground font-mono text-xs">/ month</span>
              </div>
              <div className="font-mono text-[11px] text-muted-foreground mb-4">Agency-grade automation · 25+ employees</div>
              <div className="border-2 border-good bg-good/10 p-3 mb-6 font-mono text-[11px] leading-relaxed">
                <div className="font-black mb-1 text-good">Labor savings: ~$4,100/mo</div>
                Net effective cost: <b>~$400/mo</b> after Concierge + Client Billing + Remote Monitoring recoup admin hours.
              </div>
              <ul className="font-mono text-sm space-y-2.5 mb-8 flex-1" data-testid="pricing-plus-features">
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Everything in Pro</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> AI Office Manager Concierge (~4 hrs/wk saved)</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Client Billing packets (~8 hrs/mo saved)</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Remote Work Monitoring (15-min heartbeats)</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> IP geolocation + unusual-login flags</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> White-glove onboarding</li>
                <li className="flex items-start gap-2"><span className="text-good font-black">✓</span> Priority founder support</li>
              </ul>
              <Link to="/register?tier=pro_plus" data-testid="pricing-plus-cta" className="block border-2 border-warn bg-warn text-ink px-6 py-3 font-mono uppercase text-xs tracking-widest text-center hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform">Request Pro Plus →</Link>
              <div className="font-mono text-[10px] text-muted-foreground mt-3 text-center">Concierge onboarding — talk to the founder in 1 business day.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20 text-center">
          <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tight mb-6">Start free. Print checks tonight.</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-10">No card required. Create an account, add your first employee, run payroll, and download printable check PDFs in under 5 minutes.</p>
          <Link data-testid="cta-final-register" to="/register" className="inline-flex items-center gap-3 bg-white text-ink px-8 py-4 font-mono uppercase tracking-widest text-sm border-2 border-white hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform">
            Create Company Account <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="border-t-2 border-white bg-ink text-white/60">
        <DisclaimerBand dark />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 space-y-5">
          <ComplianceDisclosures compact />
          <div className="flex flex-col md:flex-row justify-between gap-3 font-mono text-xs border-t border-white/20 pt-5">
          <div>© 2026 CHECKMATE PAYROLL · US PAYROLL</div>
          <div className="flex flex-wrap gap-4">
            <Link data-testid="footer-legal-disclaimer-link" to="/legal/disclaimer" className="underline hover:text-white">Disclaimer</Link>
            <a data-testid="footer-handbook-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/handbook/public.pdf`}
               target="_blank" rel="noreferrer" className="underline hover:text-white">Director's Handbook (PDF)</a>
            <Link data-testid="footer-privacy-link" to="/legal/privacy" className="underline hover:text-white">Privacy</Link>
            <Link data-testid="footer-terms-link" to="/legal/terms" className="underline hover:text-white">Terms</Link>
          </div>
          <div>Not tax advice. Consult a licensed CPA.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
